import axios from "axios";
import { OpenAPI } from "./core/OpenAPI";
import { getCsrfToken } from "../utils/csrf";

// パノラマステージングAPIのベースパス
const ROOM_DESIGNS_PATH = "/api/room-designs";

// APIレスポンスの型定義
export interface PanoramaAfterImage {
  id: number;
  file_name: string;
  file: string;
  downloadurl: string;
}

export interface PanoramaBeforeImage {
  id: number;
  panorama_design_id: number;
  file_name: string;
  file: string;
  render_id: string;
  regeneration_count: number;
  afterimages: PanoramaAfterImage[];
}

export interface PanoramaProcessResponse {
  success?: boolean;
  message?: string;
  id: number;
  panorama_design_id: number;
  file_name: string;
  file: string;
  render_id: string;
  regeneration_count: number;
  afterimages: PanoramaAfterImage[];
}

export interface PanoramaRegenResponse {
  success?: boolean;
  message?: string;
  id: number;
  panorama_design_id: number;
  file_name: string;
  file: string;
  render_id: string;
  regeneration_count: number;
  afterimages: PanoramaAfterImage[];
}

export interface PanoramaIncrementResponse {
  success: boolean;
  regeneration_count?: number;
  message?: string;
}

export interface PanoramaClearResponse {
  success?: boolean;
  message?: string;
  id: number;
  panorama_design_id: number;
  file_name: string;
  file: string;
  render_id: string;
  regeneration_count: number;
  afterimages: PanoramaAfterImage[];
}

export const PanoramaService = {
  /**
   * パノラマステージング初回生成API
   * POST /api/room-designs/panorama/process/
   */
  processPanorama: async (stubId: string, beforeImage: File): Promise<PanoramaProcessResponse> => {
    const form = new FormData();
    form.append('stub_id', stubId);
    form.append('before_image', beforeImage);

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/panorama/process/`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" } as any;
    }
  },

  /**
   * パノラマステージング再生成API
   * POST /api/room-designs/panorama/regen/<id>/
   * @param beforeImageId - BeforeImageのID
   * @param isInitial - 初回生成の2,3枚目の場合はtrue（DB上でis_initial_generation=trueで保存）
   */
  regenPanorama: async (beforeImageId: number, isInitial: boolean = false): Promise<PanoramaRegenResponse> => {
    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/panorama/regen/${beforeImageId}/`,
        { is_initial: isInitial },
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" } as any;
    }
  },

  /**
   * 再生成カウント増加API
   * POST /api/room-designs/panorama/increment-regen/<id>/
   */
  incrementRegenCount: async (beforeImageId: number): Promise<PanoramaIncrementResponse> => {
    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/panorama/increment-regen/${beforeImageId}/`,
        {},
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" };
    }
  },

  /**
   * 生成画像クリアAPI
   * POST /api/room-designs/panorama/clear/<id>/
   */
  clearPanorama: async (beforeImageId: number): Promise<PanoramaClearResponse> => {
    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/panorama/clear/${beforeImageId}/`,
        {},
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" } as any;
    }
  },
};
